import type { FinancialData } from "../types/FinancialInterface";

function isConsolidated(nature: unknown): boolean {
  return String(nature ?? "").toUpperCase() === "CONSOLIDATED";
}

function yearValue(year: unknown): number {
  if (year == null || year === "") return 0;
  const t = new Date(String(year)).valueOf();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Pick top financial records for display:
 * - Decide nature from the latest year (same year → CONSOLIDATED wins)
 * - Show only that nature for both filed data and uploads
 * - Up to 3 distinct years, returned oldest → newest
 */
export function getDefaultRecords(records: any[]): any[] {
  if (!records?.length) return [];

  // 1. Newest → oldest
  const sortedDesc = [...records].sort(
    (a, b) => yearValue(b.year) - yearValue(a.year)
  );

  // 2. Nature of the latest year: CONSOLIDATED preferred when both exist for that year
  const latestYear = sortedDesc[0]?.year;
  const latestYearRecords = sortedDesc.filter(
    (r) => String(r.year) === String(latestYear)
  );
  const latestHasCons = latestYearRecords.some((r) => isConsolidated(r.nature));
  const preferredIsCons = latestHasCons
    ? true
    : latestYearRecords.some((r) => !isConsolidated(r.nature))
      ? false
      : true;

  const matchesPreferredNature = (r: any) =>
    preferredIsCons ? isConsolidated(r.nature) : !isConsolidated(r.nature);

  const preferredOnly = sortedDesc.filter(matchesPreferredNature);

  // 3. Uploads: same preferred nature, latest 3 distinct years
  const uploads: FinancialData[] = [];
  const uploadYears = new Set<string>();
  for (const rec of preferredOnly) {
    if (rec.filing_type !== "upload") continue;
    const y = String(rec.year ?? "");
    if (!y || uploadYears.has(y)) continue;
    uploads.push(rec);
    uploadYears.add(y);
    if (uploads.length === 3) break;
  }

  // 4. Base (non-upload): same preferred nature, latest 3
  const base = preferredOnly
    .filter((r) => r.filing_type !== "upload")
    .slice(0, 3);

  // 5. Merge + dedupe on year|filing_type|nature
  const merged: FinancialData[] = [];
  const seen = new Set<string>();
  for (const rec of [...uploads, ...base]) {
    const key = `${rec.year}|${rec.filing_type}|${rec.nature}`;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(rec);
  }

  // 6. Oldest → newest for display
  return merged.sort((a, b) => yearValue(a.year) - yearValue(b.year));
}
